import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  HelpCircle,
  Camera,
  X,
  Image as ImageIcon,
  AlertCircle,
} from 'lucide-react';
import { formatTime } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import TacoV2Panel from '../components/TacoV2Panel';

const TACO_MASCOT_URL = "https://customer-assets.emergentagent.com/job_ttouchmanage/artifacts/sua7mu0g_Untitled%20design%20%288%29.png";

// Max file size: 4MB
const MAX_FILE_SIZE = 4 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const AIAssistantPage = () => {
  const [searchParams] = useSearchParams();
  const { api, user, isStaff, isAdmin } = useAuth();
  
  // V2 status check
  const [v2Enabled, setV2Enabled] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [userJobs, setUserJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('none');
  const [showJobSelect, setShowJobSelect] = useState(false);
  const [preselectedJob, setPreselectedJob] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Staff vs Director quick questions
  const isStaffUser = isStaff && !isAdmin;
  
  const quickQuestions = useMemo(() => {
    const staffQuickQuestions = [
      "What jobs do I have today?",
      "Any tips for cleaning glass?",
      "How do I mark my arrival?",
      "What should I do if a client isn't home?",
    ];
    
    const directorQuickQuestions = [
      "What jobs do we have today?",
      "Who is working tomorrow?",
      "How do I create a new booking?",
      "How do I reschedule a clean?",
    ];
    
    return isStaffUser ? staffQuickQuestions : directorQuickQuestions;
  }, [isStaffUser]);

  // Check if v2 is enabled - MUST be called unconditionally
  useEffect(() => {
    const checkV2 = async () => {
      try {
        const res = await api().get('/taco/v2/health');
        setV2Enabled(res.data.v2_enabled);
      } catch {
        setV2Enabled(false);
      }
    };
    checkV2();
  }, [api]);

  // Handle job_id from URL params (from Job Hub navigation)
  useEffect(() => {
    if (v2Enabled !== false) return; // Only run for legacy mode
    
    const jobIdFromUrl = searchParams.get('job_id');
    if (jobIdFromUrl) {
      setSelectedJobId(jobIdFromUrl);
      setShowJobSelect(true);
      // Fetch the job details for display
      api().get(`/bookings/${jobIdFromUrl}`)
        .then(res => setPreselectedJob(res.data))
        .catch(() => {});
    }
  }, [searchParams, api, v2Enabled]);

  // Fetch user's jobs for the dropdown
  useEffect(() => {
    if (v2Enabled !== false) return; // Only run for legacy mode
    
    const fetchJobs = async () => {
      try {
        const response = await api().get('/bookings');
        // API returns {bookings: [...], total: ...}
        const bookingsData = response.data.bookings || response.data || [];
        // For staff, filter to their assigned jobs only
        const jobs = isStaffUser 
          ? bookingsData.filter(j => j.assigned_staff_id === user?.id && j.status !== 'completed')
          : bookingsData.filter(j => j.status !== 'completed');
        setUserJobs(jobs.slice(0, 20)); // Limit to 20 recent jobs
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      }
    };
    fetchJobs();
  }, [api, user, isStaffUser, v2Enabled]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    const validFiles = files.filter(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Only JPG, PNG, and WebP images are allowed`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: Image must be under 4MB`);
        return false;
      }
      return true;
    });
    
    // Check total images limit
    const totalImages = selectedImages.length + validFiles.length;
    if (totalImages > 5) {
      toast.error('Maximum 5 images per message');
      return;
    }
    
    // Add to selected images
    setSelectedImages(prev => [...prev, ...validFiles]);
    
    // Generate previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreviews(prev => [...prev, {
          name: file.name,
          url: e.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Show job select when images are added
    if (validFiles.length > 0) {
      setShowJobSelect(true);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedImages.length]);

  const removeImage = useCallback((index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    
    if (selectedImages.length <= 1) {
      setShowJobSelect(false);
      setSelectedJobId('none');
    }
  }, [selectedImages.length]);

  const convertToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const sendMessage = useCallback(async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() && selectedImages.length === 0) return;

    const hasImages = selectedImages.length > 0;
    const messageContent = newMessage.trim() || (hasImages ? 'Please analyze this image' : '');
    
    const userMessage = {
      id: Date.now().toString(),
      content: messageContent,
      is_user: true,
      timestamp: new Date().toISOString(),
      images: imagePreviews.map(p => p.url),
      image_count: selectedImages.length,
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setSending(true);

    try {
      let response;
      
      if (hasImages) {
        // Convert images to base64
        const base64Images = await Promise.all(
          selectedImages.map(file => convertToBase64(file))
        );
        
        response = await api().post('/assistant/chat-with-images', {
          message: messageContent,
          session_id: sessionId,
          images: base64Images,
          job_id: selectedJobId !== 'none' ? selectedJobId : null,
        });
        
        // Clear images after sending
        setSelectedImages([]);
        setImagePreviews([]);
        setShowJobSelect(false);
        setSelectedJobId('none');
      } else {
        response = await api().post('/assistant/chat', {
          message: messageContent,
          session_id: sessionId,
        });
      }

      const aiMessage = {
        id: (Date.now() + 1).toString(),
        content: response.data.response,
        is_user: false,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, aiMessage]);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Failed to get AI response:', error);
      toast.error('Failed to get response. Please try again.');
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        content: "Sorry, I'm having trouble right now. Please try again in a moment.",
        is_user: false,
        timestamp: new Date().toISOString(),
        is_error: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSending(false);
    }
  }, [newMessage, selectedImages, imagePreviews, sessionId, selectedJobId, api, convertToBase64]);

  const handleQuickQuestion = useCallback((question) => {
    setNewMessage(question);
  }, []);

  // If v2 is enabled, render the hybrid panel
  if (v2Enabled === true) {
    return <TacoV2Panel />;
  }
  
  // If still checking, show loading
  if (v2Enabled === null) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Legacy chat UI (v2Enabled === false)
  return (
    <div className="h-[calc(100vh-180px)] md:h-[calc(100vh-140px)] pb-16 md:pb-0">
      <Card className="border-zinc-100 h-full flex flex-col">
        {/* Header */}
        <CardHeader className="pb-2 border-b border-zinc-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img 
              src={TACO_MASCOT_URL} 
              alt="Taco" 
              className="h-12 w-12 rounded-full object-cover border-2 border-amber-200"
            />
            <div>
              <CardTitle className="text-xl">Taco</CardTitle>
              <p className="text-sm text-zinc-500">Your helpful AI assistant</p>
            </div>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="flex-1 p-0 overflow-hidden">
          <ScrollArea className="h-full p-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <img 
                  src={TACO_MASCOT_URL} 
                  alt="Taco" 
                  className="h-24 w-24 rounded-full object-cover border-4 border-amber-100 mb-4"
                />
                <h3 className="text-xl font-semibold text-zinc-900 mb-2">
                  G'day, {user?.full_name?.split(' ')[0]}!
                </h3>
                
                {/* Show job context if coming from Job Hub */}
                {preselectedJob && (
                  <div className="w-full max-w-md mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800">
                      Job Context: {preselectedJob.client_name || 'Client'}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {preselectedJob.service_address}
                    </p>
                    <p className="text-xs text-blue-500 mt-1">
                      Upload a photo and I'll help with this job!
                    </p>
                  </div>
                )}
                
                <p className="text-zinc-500 mb-6 max-w-md">
                  {preselectedJob 
                    ? "Upload a photo of any stain, surface, or problem area and I'll provide cleaning guidance for this job."
                    : isStaffUser 
                      ? "I'm Taco! I'm here to help you with your jobs, cleaning tips, and how to use the app. You can also upload photos for cleaning guidance!"
                      : "I'm Taco, your AI assistant! Ask me about bookings, staff schedules, clients, or upload photos for cleaning analysis."
                  }
                </p>
                
                {/* Photo upload tip */}
                <div className="w-full max-w-md mb-6 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <Camera className="h-4 w-4" />
                    <span className="font-medium">New! Photo Analysis</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Upload photos of stains, surfaces, or problem areas and I'll provide cleaning guidance.
                  </p>
                </div>
                
                <div className="w-full max-w-md">
                  <p className="text-sm font-medium text-zinc-700 mb-3 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" />
                    Quick questions
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {quickQuestions.map((question) => (
                      <Button
                        key={question}
                        variant="outline"
                        className="text-left justify-start h-auto py-3 px-4 text-sm"
                        onClick={() => handleQuickQuestion(question)}
                        data-testid={`quick-question-${question.slice(0, 20)}`}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'flex',
                      message.is_user ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {!message.is_user && (
                      <img 
                        src={TACO_MASCOT_URL} 
                        alt="Taco" 
                        className="h-8 w-8 rounded-full object-cover border border-amber-200 mr-2 flex-shrink-0"
                      />
                    )}
                    <div className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-3',
                      message.is_user 
                        ? 'bg-zinc-900 text-white rounded-br-md'
                        : message.is_error
                          ? 'bg-red-50 border border-red-200 rounded-bl-md'
                          : 'bg-amber-50 border border-amber-100 rounded-bl-md'
                    )}>
                      {/* Show image thumbnails in user messages */}
                      {message.is_user && message.images && message.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {message.images.map((img, idx) => (
                            <img 
                              key={idx}
                              src={img} 
                              alt={`Uploaded ${idx + 1}`}
                              className="h-16 w-16 object-cover rounded-lg"
                            />
                          ))}
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className={cn(
                        'text-xs mt-2',
                        message.is_user ? 'text-white/60' : 'text-zinc-400'
                      )}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <img 
                      src={TACO_MASCOT_URL} 
                      alt="Taco" 
                      className="h-8 w-8 rounded-full object-cover border border-amber-200 mr-2"
                    />
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                        <span className="text-zinc-500">
                          {selectedImages.length > 0 ? 'Taco is analyzing your photo...' : 'Taco is thinking...'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
        </CardContent>

        {/* Image Previews */}
        {imagePreviews.length > 0 && (
          <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="h-4 w-4 text-zinc-500" />
              <span className="text-sm text-zinc-600">{imagePreviews.length} image(s) selected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={preview.url} 
                    alt={preview.name}
                    className="h-16 w-16 object-cover rounded-lg border border-zinc-200"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-image-${index}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Job attachment dropdown */}
            {showJobSelect && userJobs.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-zinc-500">Attach to job:</span>
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="job-select">
                    <SelectValue placeholder="Select job (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No job attachment</SelectItem>
                    {userJobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.client_name || 'Unknown'} - {new Date(job.scheduled_date).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Privacy note */}
            <div className="flex items-center gap-1 mt-2 text-xs text-zinc-400">
              <AlertCircle className="h-3 w-3" />
              <span>Avoid uploading personal documents or sensitive info.</span>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-zinc-100 flex-shrink-0">
          <form onSubmit={sendMessage} className="flex gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleImageSelect}
              className="hidden"
              data-testid="image-upload-input"
            />
            
            {/* Photo upload button */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || selectedImages.length >= 5}
              className="flex-shrink-0"
              data-testid="upload-photo-btn"
              title="Upload photo for analysis"
            >
              <Camera className="h-4 w-4" />
            </Button>
            
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={selectedImages.length > 0 ? "Add a question about the photo..." : "Ask me anything..."}
              disabled={sending}
              className="flex-1"
              data-testid="ai-chat-input"
            />
            <Button
              type="submit"
              disabled={(!newMessage.trim() && selectedImages.length === 0) || sending}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="ai-send-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default AIAssistantPage;
